/**
 * TaxSetu AI - Plans Page with Manual Payment
 * UPI QR Code + Transaction ID Upload
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  Crown, 
  Check,
  Upload,
  QrCode,
  Copy,
  CheckCircle,
  FileText,
  Smartphone,
  Mail
} from 'lucide-react';
import { Button } from "../components/ui/button";
import ThemeToggle from "../components/ThemeToggle";
import { toast } from 'sonner';
import { API_BASE_URL } from "../utils/api";
import { useAuth } from "../context/AuthContext";

const PlansPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState('pro_monthly');
  const [transactionId, setTransactionId] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [invoiceDetails, setInvoiceDetails] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    gstin: '',
    address: ''
  });

  const UPI_ID = "taxsetu@upi";
  const UPI_QR_URL = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=upi://pay?pa=${UPI_ID}&pn=TaxSetu&am=${selectedPlan === 'pro_monthly' ? '499' : '3999'}&cu=INR`;

  const plans = [
    {
      id: 'pro_monthly',
      name: 'Pro Monthly',
      price: 499,
      period: '/month',
      features: [
        'Unlimited AI queries',
        'Priority response',
        'Document analysis',
        'Chat history forever'
      ]
    },
    {
      id: 'pro_yearly',
      name: 'Pro Yearly',
      price: 3999,
      originalPrice: 5988,
      period: '/year',
      badge: 'Save ₹2000',
      features: [
        'Everything in Monthly',
        'Priority support',
        'Early access features',
        'Exclusive webinars'
      ]
    }
  ];

  const copyUPI = () => {
    navigator.clipboard.writeText(UPI_ID);
    toast.success('UPI ID copied!');
  };

  const handleTransactionSubmit = async () => {
    if (!transactionId.trim()) {
      toast.error('Please enter transaction ID');
      return;
    }

    setUploading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/payment/manual-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transaction_id: transactionId,
          plan_id: selectedPlan,
          user_email: user?.email || 'guest',
          amount: selectedPlan === 'pro_monthly' ? 499 : 3999
        })
      });

      if (response.ok) {
        setSubmitted(true);
        toast.success('Payment submitted! We\'ll verify and activate your Pro within 24 hours.');
      } else {
        toast.error('Submission failed. Please try again.');
      }
    } catch (error) {
      toast.error('Submission failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleInvoiceRequest = async () => {
    if (!invoiceDetails.email || !invoiceDetails.phone) {
      toast.error('Please fill required fields');
      return;
    }

    setUploading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/payment/request-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...invoiceDetails,
          plan_id: selectedPlan,
          amount: selectedPlan === 'pro_monthly' ? 499 : 3999
        })
      });

      if (response.ok) {
        toast.success('Invoice request submitted! We\'ll send it to your email within 24 hours.');
        setShowInvoiceForm(false);
      } else {
        toast.error('Request failed. Please try again.');
      }
    } catch (error) {
      toast.error('Request failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const selectedPlanData = plans.find(p => p.id === selectedPlan);

  return (
    <div className="min-h-screen bg-slate-900 mesh-gradient">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold text-white">Upgrade to Pro</h1>
          </div>
          <ThemeToggle />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {submitted ? (
          /* Success State */
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-12"
          >
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Payment Submitted! 🎉</h2>
            <p className="text-slate-400 mb-6">
              We'll verify your payment and activate Pro within 24 hours.
              <br />You'll receive a confirmation email.
            </p>
            <Button onClick={() => navigate('/chat')} className="btn-primary text-white">
              Back to Chat
            </Button>
          </motion.div>
        ) : (
          <div className="grid md:grid-cols-2 gap-8">
            {/* Left - Plan Selection */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4">Select Plan</h2>
              <div className="space-y-3">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`w-full p-4 rounded-xl border text-left transition-all ${
                      selectedPlan === plan.id
                        ? 'bg-cyan-500/10 border-cyan-500'
                        : 'bg-slate-800/50 border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white">{plan.name}</span>
                        {plan.badge && (
                          <span className="text-xs px-2 py-0.5 bg-amber-500 text-white rounded-full">
                            {plan.badge}
                          </span>
                        )}
                      </div>
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        selectedPlan === plan.id ? 'border-cyan-500 bg-cyan-500' : 'border-slate-600'
                      }`}>
                        {selectedPlan === plan.id && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-2xl font-bold text-white">₹{plan.price}</span>
                      {plan.originalPrice && (
                        <span className="text-slate-500 line-through text-sm">₹{plan.originalPrice}</span>
                      )}
                      <span className="text-slate-400 text-sm">{plan.period}</span>
                    </div>
                    <ul className="space-y-1">
                      {plan.features.slice(0, 3).map((feature, i) => (
                        <li key={i} className="text-sm text-slate-400 flex items-center gap-2">
                          <Check className="w-3 h-3 text-cyan-400" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </button>
                ))}
              </div>
            </div>

            {/* Right - Payment */}
            <div>
              <h2 className="text-lg font-semibold text-white mb-4">Pay via UPI</h2>
              
              {/* UPI QR Code */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-6 text-center mb-4">
                <div className="bg-white p-4 rounded-xl inline-block mb-4">
                  <img 
                    src={UPI_QR_URL} 
                    alt="UPI QR Code" 
                    className="w-48 h-48"
                  />
                </div>
                <p className="text-slate-400 text-sm mb-2">Scan with any UPI app</p>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <code className="px-3 py-1 bg-slate-900 rounded text-cyan-400 text-sm">
                    {UPI_ID}
                  </code>
                  <button
                    onClick={copyUPI}
                    className="p-1 text-slate-400 hover:text-white"
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
                <div className="text-2xl font-bold text-white">
                  ₹{selectedPlanData?.price}
                </div>
              </div>

              {/* Transaction ID Input */}
              <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 mb-4">
                <label className="text-sm text-slate-400 mb-2 block">
                  After payment, enter Transaction ID / UTR
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={transactionId}
                    onChange={(e) => setTransactionId(e.target.value)}
                    placeholder="e.g., 123456789012"
                    className="flex-1 px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                  />
                  <Button
                    onClick={handleTransactionSubmit}
                    disabled={uploading || !transactionId.trim()}
                    className="btn-primary text-white"
                  >
                    {uploading ? (
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>

              {/* Request Invoice */}
              {!showInvoiceForm ? (
                <button
                  onClick={() => setShowInvoiceForm(true)}
                  className="w-full text-sm text-cyan-400 hover:text-cyan-300 flex items-center justify-center gap-2"
                >
                  <FileText className="w-4 h-4" />
                  Request Invoice Instead
                </button>
              ) : (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3"
                >
                  <h3 className="font-medium text-white">Invoice Details</h3>
                  <input
                    type="text"
                    value={invoiceDetails.name}
                    onChange={(e) => setInvoiceDetails({...invoiceDetails, name: e.target.value})}
                    placeholder="Full Name *"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                  />
                  <input
                    type="email"
                    value={invoiceDetails.email}
                    onChange={(e) => setInvoiceDetails({...invoiceDetails, email: e.target.value})}
                    placeholder="Email *"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                  />
                  <input
                    type="tel"
                    value={invoiceDetails.phone}
                    onChange={(e) => setInvoiceDetails({...invoiceDetails, phone: e.target.value})}
                    placeholder="Phone *"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                  />
                  <input
                    type="text"
                    value={invoiceDetails.gstin}
                    onChange={(e) => setInvoiceDetails({...invoiceDetails, gstin: e.target.value})}
                    placeholder="GSTIN (optional)"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm"
                  />
                  <textarea
                    value={invoiceDetails.address}
                    onChange={(e) => setInvoiceDetails({...invoiceDetails, address: e.target.value})}
                    placeholder="Billing Address"
                    rows={2}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm resize-none"
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={() => setShowInvoiceForm(false)}
                      variant="ghost"
                      className="flex-1 text-slate-400"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleInvoiceRequest}
                      disabled={uploading}
                      className="flex-1 btn-primary text-white"
                    >
                      Request Invoice
                    </Button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default PlansPage;
